import { ChangeEvent, DragEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import { Badge, Box, Button, Flex, Input, Label, Spinner, Text, Textarea, useColorMode } from "theme-ui";
import { FaFileAlt, FaInfoCircle, FaPaperclip, FaPencilAlt, FaTimes, FaTrashAlt } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import moment from "moment";
import toast from "react-hot-toast";
import DataTable from "../../Tabels/Tabel";
import { uploadMedia } from "../../../store/api";
import { uploadIcon } from "../../../helper/uploadType";
import FormSelect from "../../forms-component/globalSelect";
import { DeletePopModule } from "../../DeletePopModule/DeletePopModule";
import { ColumnDef } from "@tanstack/react-table";
import {
    addDescriptionActionHighlight,
    createActionHighlight,
    deleteActionHighlight,
    editActionHighlight,
    getActionByName,
    getActionHighlighterIds
} from "../../../store/actions/action.Highlighters";
import { ActionResponse, ActiveActionItem, DescriptionItem, NewDeliveryNotesModalProps, RootState, TaskTabItem } from "../../../../@types";
import { downloadAttachments, InlineEditCell } from "../../../utils/GenerateQueryString";
import { linkifyText } from "../../../utils/textFormat";

const getUserInitials = (name?: string, auther?: { FirstName?: string; LastName?: string }) => {
    if (!name) {
        if (auther?.FirstName || auther?.LastName) {
            return `${auther?.FirstName?.[0] || ''}${auther?.LastName?.[0] || ''}`.toUpperCase();
        }
        return "-";
    }
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
};

const WHOLE_STAGE_OPTION = "whole-stage";

export const NewDeliveryNotesModal = ({
    selectedHighlight = { name: "Delivery Notes" },
    showModal,
    setShowModal,
    actionObject,
    TaskView,
    activeTab: initialActiveTab = 0,
}: NewDeliveryNotesModalProps) => {
    const dispatch = useDispatch();
    const { stageId } = useParams<{ stageId: string }>();
    const [colorMode] = useColorMode();
    const userData = useSelector((state: RootState & { user?: { userData?: Array<{ _id: string; FirstName?: string; LastName?: string }> | { data?: Array<{ _id: string; FirstName?: string; LastName?: string }> } } }) => state.user?.userData);
    const stageAllTaskView = useSelector((state: RootState) => (state.project as unknown as Record<string, unknown>)?.stageAllTaskView);
    const tasksView = useSelector((state: RootState) => (state.project as unknown as Record<string, unknown>)?.tasksView);
    const stagesView = useSelector((state: RootState) => (state.project as unknown as Record<string, unknown>)?.stagesView);
    const { actionHighlights, loader } = useSelector((state: RootState & { actionHighlight: { actionHighlights: ActiveActionItem; loader: boolean } }) => state.actionHighlight);
    const [activeTab, setActiveTab] = useState<number>(initialActiveTab);
    const [selectedRevision, setSelectedRevision] = useState<string>("");
    const [noteText, setNoteText] = useState<string>("");
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const editDraftRef = useRef<string | null>(null);
    const [noteError, setNoteError] = useState<string>("");
    const [isDeleteModelOpen, setIsDeleteModelOpen] = useState<boolean>(false);
    const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
    const [isAdding, setIsAdding] = useState<boolean>(false);
    const isAddingRef = useRef<boolean>(false);
    const [fetchedRevisions, setFetchedRevisions] = useState<ActiveActionItem[]>([]);
    const [fetchedPlanChecks, setFetchedPlanChecks] = useState<ActiveActionItem[]>([]);
    const [attachments, setAttachments] = useState<string[]>([]);
    const [isAttachModalOpen, setIsAttachModalOpen] = useState<boolean>(false);
    const [modalAttachments, setModalAttachments] = useState<string[]>([]);
    const [isModalUploading, setIsModalUploading] = useState<boolean>(false);
    const [modalUploadError, setModalUploadError] = useState<string>("");
    const [isDraggingModal, setIsDraggingModal] = useState<boolean>(false);
    const [rowAttachItem, setRowAttachItem] = useState<DescriptionItem | null>(null);
    // completed notes are read-only: their attachments can be viewed and downloaded but not changed
    const [isRowAttachLocked, setIsRowAttachLocked] = useState<boolean>(false);
    const [selectedModalIndices, setSelectedModalIndices] = useState<number[]>([]);
    const [isDownloadingFiles, setIsDownloadingFiles] = useState<boolean>(false);
    const [isModalSaving, setIsModalSaving] = useState<boolean>(false);
    const [skipTableLoader, setSkipTableLoader] = useState<boolean>(false);
    // the store keeps the previous tab's notes until the new response arrives, so hide them until then
    const [isTabDataStale, setIsTabDataStale] = useState<boolean>(true);
    const tabRequestId = useRef<number>(0);
    const taskTabs = TaskView || stageAllTaskView || [];

    useEffect(() => {
        if (!loader) {
            setSkipTableLoader(false);
        }
    }, [loader]);

    const currentActionObject = useMemo(() => {
        const projId = actionObject?.projectId || (tasksView as Record<string, unknown>)?.projectId as { _id?: string } | undefined;
        const stgId = ((tasksView as Record<string, unknown>)?.stageDataId as { _id?: string } | undefined)?._id || actionObject?.stageId || stageId;
        if (activeTab === 0) {
            return { projectId: projId, stageId: stgId, taskId: null };
        } else {
            const selectedTask = (taskTabs as TaskTabItem[])[activeTab - 1] as (TaskTabItem & Record<string, unknown>) | undefined;
            const taskIdVal = selectedTask?.taskId;
            const nestedId = typeof taskIdVal === 'object' && taskIdVal !== null ? (taskIdVal as { _id?: string })._id : (typeof taskIdVal === 'string' ? taskIdVal : null);
            const selectedTaskId = selectedTask?.id || selectedTask?._id || nestedId || (selectedTask?.taskDataId as string) || null;
            return { projectId: projId, stageId: stgId, taskId: selectedTaskId };
        }
    }, [actionObject?.projectId, actionObject?.stageId, actionObject?.taskId, tasksView, stageId, activeTab, taskTabs]);

    const currentHighlighters = useMemo(() => {
        const stageRaw = (stagesView as Record<string, unknown>)?.highlighters || ((tasksView as Record<string, unknown>)?.stageDataId as Record<string, unknown>)?.highlighters || ((tasksView as Record<string, unknown>)?.stageId as Record<string, unknown>)?.highlighters || actionObject?.highlighters || [];
        let taskRaw: Array<{ name?: string } | string> = [];
        if (activeTab > 0) {
            const selectedTask = (taskTabs as TaskTabItem[])[activeTab - 1];
            const selectedTaskId = selectedTask?.id || selectedTask?._id;
            const taskFromStore = Array.isArray(stageAllTaskView) ? (stageAllTaskView as TaskTabItem[]).find((t) => t._id === selectedTaskId || t.id === selectedTaskId) : null;
            taskRaw = taskFromStore?.highlighters || selectedTask?.highlighters || ((tasksView as Record<string, unknown>)?._id === selectedTaskId ? (tasksView as Record<string, unknown>)?.highlighters as Array<{ name?: string } | string> : []) || [];
        }
        const combined = [...stageRaw, ...taskRaw];
        return combined.map((h: { name?: string } | string) => (typeof h === "string" ? h : h?.name)).filter((name): name is string => Boolean(name));
    }, [activeTab, stagesView, tasksView, taskTabs, stageAllTaskView, actionObject]);

    const hasRevision = useMemo(() => currentHighlighters.some(h => h.toLowerCase().includes("revision")), [currentHighlighters]);
    const hasPlanCheck = useMemo(() => currentHighlighters.some(h => h.toLowerCase().includes("plan check")), [currentHighlighters]);
    const targetProjId = currentActionObject?.projectId;
    const targetStgId = currentActionObject?.stageId;
    const targetTaskId = currentActionObject?.taskId;

    useEffect(() => {
        if (showModal) {
            setNoteText("");
            setSelectedRevision("");
            setNoteError("");
            setEditingIndex(null);
            setAttachments([]);
            setModalAttachments([]);
            setModalUploadError("");

            const activeActionObj = {
                projectId: targetProjId,
                stageId: targetStgId,
                taskId: targetTaskId,
                isActive: true
            };
            if (hasRevision) {
                dispatch(getActionByName(activeActionObj, { name: "Revision" }, (res: ActionResponse) => {
                    setFetchedRevisions(res?.data?.data || []);
                }));
            } else {
                setFetchedRevisions([]);
            }

            if (hasPlanCheck) {
                dispatch(getActionByName(activeActionObj, { name: "Plan Check" }, (res: ActionResponse) => {
                    setFetchedPlanChecks(res?.data?.data || []);
                }));
            } else {
                setFetchedPlanChecks([]);
            }

            if (targetProjId) {
                const requestId = ++tabRequestId.current;
                dispatch(getActionHighlighterIds(currentActionObject, { name: "Delivery Notes" }, () => {
                    if (requestId === tabRequestId.current) setIsTabDataStale(false);
                }));
            } else {
                setIsTabDataStale(false);
            }
        }
    }, [dispatch, showModal, targetProjId, targetStgId, targetTaskId, hasRevision, hasPlanCheck, activeTab]);

    const refetchData = useCallback(() => {
        if (currentActionObject?.projectId) {
            dispatch(getActionHighlighterIds(currentActionObject, { name: "Delivery Notes" }));
        }
    }, [dispatch, currentActionObject]);

    const isTaskMatch = useCallback((item: ActiveActionItem, currentTaskId: string | null | undefined) => {
        if (!currentTaskId) return true;
        const payload = item?.payload || {};
        const rawTaskIds = payload.taskId ?? item?.taskId;

        if (rawTaskIds === null || rawTaskIds === undefined) return true;

        if (typeof rawTaskIds === "string") {
            return rawTaskIds === currentTaskId;
        }

        if (typeof rawTaskIds === "object" && rawTaskIds !== null && "_id" in rawTaskIds) {
            return (rawTaskIds as { _id?: string })._id === currentTaskId;
        }

        if (Array.isArray(rawTaskIds)) {
            return rawTaskIds.some((t: unknown) => {
                if (typeof t === "string") return t === currentTaskId;
                if (typeof t === "object" && t !== null && "_id" in (t as Record<string, unknown>)) {
                    return (t as { _id?: string })._id === currentTaskId;
                }
                return false;
            });
        }

        return false;
    }, []);

    const activeRevPcOptions = useMemo(() => {
        if (activeTab === 0) return [];

        const options: { label: string; value: string; createdAt: number; isRevision: boolean }[] = [];
        const addActiveItems = (list: ActiveActionItem[], isRevision: boolean) => {
            if (!Array.isArray(list)) return;
            list.forEach((item: ActiveActionItem) => {
                const payload = item?.payload || {};
                const isCompleted = payload.isCompleted === true || item.isCompleted === true;
                const matchesTask = isTaskMatch(item, targetTaskId);
                const rawTaskIds = payload.taskId ?? item?.taskId;
                const isTaskSpecific = rawTaskIds !== null && rawTaskIds !== undefined;
                if (!isCompleted && matchesTask && isTaskSpecific) {
                    const name = payload.name || item.name || payload.description;
                    const itemId = item._id || item.id;
                    if (name && typeof name === "string" && itemId && !options.some(opt => opt.value === itemId)) {
                        options.push({ label: name.trim(), value: itemId, createdAt: new Date((item.createdAt as string | undefined) || 0).getTime(), isRevision });
                    }
                }
            });
        };

        if (hasRevision) addActiveItems(fetchedRevisions, true);
        if (hasPlanCheck) addActiveItems(fetchedPlanChecks, false);

        return options;
    }, [activeTab, hasRevision, hasPlanCheck, fetchedRevisions, fetchedPlanChecks, targetTaskId, isTaskMatch]);

    // default: the newest revision, or the newest plan check only when there is no active revision (a newer plan check never wins);
    // the stable sort keeps list order for equal or missing dates
    const latestRevPcId = useMemo(() => {
        const newest = (isRevision: boolean) => activeRevPcOptions.filter(opt => opt.isRevision === isRevision).sort((a, b) => a.createdAt - b.createdAt).pop()?.value;
        return newest(true) || newest(false);
    }, [activeRevPcOptions]);

    // selectedRevision is only the user's own choice; until they pick (or if the pick is no longer listed) the default applies,
    // so it follows the data even when the revisions arrive after the tab opens
    const isChoiceValid = selectedRevision === WHOLE_STAGE_OPTION || activeRevPcOptions.some(opt => opt.value === selectedRevision);
    const effectiveRevision = selectedRevision && isChoiceValid ? selectedRevision : latestRevPcId || WHOLE_STAGE_OPTION;

    const handleTabChange = (newTab: number) => {
        if (newTab === activeTab) return;
        setActiveTab(newTab);
        setIsTabDataStale(true);
        setFetchedRevisions([]);
        setFetchedPlanChecks([]);
        setNoteText("");
        setSelectedRevision("");
        setNoteError("");
        setEditingIndex(null);
    };

    const uploadModalFilesList = async (files: FileList | File[]) => {
        if (!files || files.length === 0) return;
        setIsModalUploading(true);
        setModalUploadError("");
        const uploadedPaths = [...modalAttachments];
        try {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const formData = new FormData();
                formData.append('file', file);
                formData.append('type', 'actionAttachments');
                formData.append('uploadType', 'File');
                const response = await uploadMedia(formData);
                if (response?.data?.status && response?.data?.data) {
                    uploadedPaths.push(response.data.data);
                } else {
                    setModalUploadError('Failed to upload file');
                }
            }
            setModalAttachments(uploadedPaths);
        } catch {
            setModalUploadError('An error occurred during file upload');
        } finally {
            setIsModalUploading(false);
        }
    };

    const handleModalFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            await uploadModalFilesList(files);
            if (event.target) event.target.value = '';
        }
    };

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isModalUploading) setIsDraggingModal(true);
    };

    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingModal(false);
    };

    const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingModal(false);
        if (isModalUploading) return;
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            await uploadModalFilesList(e.dataTransfer.files);
        }
    };

    const removeModalAttachment = (indexToRemove: number) => {
        setModalAttachments(prev => prev.filter((_, idx) => idx !== indexToRemove));
    };

    const openAttachModal = () => {
        setRowAttachItem(null);
        setModalAttachments([...attachments]);
        setModalUploadError("");
        setIsDraggingModal(false);
        setSelectedModalIndices([]);
        setIsAttachModalOpen(true);
    };

    const saveModalAttachmentsLocal = () => {
        if (rowAttachItem) {
            // Save to server for existing note row — same pattern as handleSaveEdit
            const item = rowAttachItem as Record<string, unknown>;
            const noteItemId = (item._id || item.id) as string | undefined;
            const targetId = (item.parentActionHighlightId as string | undefined) || activeActionHighlightId || noteItemId;
            if (!targetId) {
                toast.error("Item ID not found");
                return;
            }
            const payload = {
                id: noteItemId,
                _id: noteItemId,
                name: (item.name || item.description) as string | undefined,
                actionType: selectedHighlight?.name || "Delivery Notes",
                projectId: currentActionObject?.projectId,
                stageId: currentActionObject?.stageId,
                taskId: currentActionObject?.taskId || null,
                data: {
                    attachment: modalAttachments,
                    revisionId: getRefId(item.revisionId) || getRefId(item.revision),
                    planCheckId: getRefId(item.planCheckId) || getRefId(item.planCheck),
                    stageId: getRefId(item.stageId) || getRefId(item.stage),
                }
            };
            setIsModalSaving(true);
            setSkipTableLoader(true);
            dispatch(editActionHighlight(payload, targetId, (res: ActionResponse) => {
                setIsModalSaving(false);
                if (res?.data?.status || res?.status === 200) {
                    toast.success("Attachments updated");
                    refetchData();
                    setIsAttachModalOpen(false);
                    setRowAttachItem(null);
                } else {
                    toast.error(res?.data?.message || "Failed to update attachments");
                    setSkipTableLoader(false);
                }
            }));
        } else {
            // Stage locally for new note
            setAttachments([...modalAttachments]);
            setIsAttachModalOpen(false);
        }
    };

    const getFileExt = (filename: string) => filename.split('.').pop()?.toLowerCase() || '';

    const toggleModal = () => {
        setShowModal(false);
        setIsTabDataStale(true);
        setNoteText("");
        setSelectedRevision("");
        setNoteError("");
        setEditingIndex(null);
        setAttachments([]);
        setModalAttachments([]);
        setModalUploadError("");
    };

    const descriptionList = useMemo(() => {
        const payloadDesc = (actionHighlights as ActiveActionItem)?.payload?.Description;
        if (payloadDesc && Array.isArray(payloadDesc)) {
            // newest first; reversing before the stable sort keeps later-added notes first on equal timestamps
            return payloadDesc
                .map((desc: DescriptionItem) => ({
                    ...desc,
                    parentActionHighlightId: (actionHighlights as ActiveActionItem)._id
                }))
                .reverse()
                .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
        }

        return [];
    }, [actionHighlights]);

    const activeActionHighlightId = useMemo(() => {
        return (actionHighlights as ActiveActionItem)?._id || null;
    }, [actionHighlights]);

    const handleAddNote = () => {
        if (isAddingRef.current || isAdding || loader || isModalUploading) return;
        let hasError = false;
        if (!noteText.trim()) {
            setNoteError("Please enter a delivery note");
            hasError = true;
        } else {
            setNoteError("");
        }

        if (hasError) return;
        const revPcId = effectiveRevision !== WHOLE_STAGE_OPTION ? effectiveRevision : null;
        const isPlanCheck = fetchedPlanChecks.some(pc => pc._id === revPcId || pc.id === revPcId);

        const newNoteItem = {
            name: noteText,
            stageId: revPcId ? null : getRefId(targetStgId),
            revisionId: isPlanCheck ? null : revPcId,
            planCheckId: isPlanCheck ? revPcId : null,
            isDone: false,
            attachment: attachments,
        };

        isAddingRef.current = true;
        setIsAdding(true);
        const onAddSuccess = (res: ActionResponse) => {
            isAddingRef.current = false;
            setIsAdding(false);
            if (res?.data?.status || res?.status === 200) {
                toast.success("Delivery note added");
                setNoteText("");
                setAttachments([]);
                setModalAttachments([]);
                setModalUploadError("");
                refetchData();
            } else {
                toast.error(res?.data?.message || res?.message || "Failed to add delivery note");
            }
        };

        const targetId = activeActionHighlightId;
        if (targetId) {
            const updateData = {
                data: newNoteItem,
                ...currentActionObject,
                actionType: "Delivery Notes",
            };
            dispatch(addDescriptionActionHighlight(updateData, targetId, onAddSuccess));
        } else {
            const payload = {
                actionType: "Delivery Notes",
                payload: {
                    Description: [newNoteItem]
                },
                ...currentActionObject
            };
            dispatch(createActionHighlight(payload, onAddSuccess));
        }
    };

    const getRefId = (val: unknown): string | null => {
        if (!val) return null;
        if (typeof val === 'string') return val;
        if (typeof val === 'object' && val !== null && '_id' in val) {
            return (val as { _id?: string })._id || null;
        }
        return null;
    };

    // const handleToggleCheck = (index: number, field: 'isChecked' | 'selected') => {
    //     const item: Record<string, unknown> | undefined = descriptionList[index] as Record<string, unknown> | undefined;
    //     if (!item) return;
    //     const itemId = (item._id || item.id) as string | undefined;
    //     const key = itemId || `${index}`;
    //     const currentVal = optimisticChecked[key] !== undefined ? optimisticChecked[key] : Boolean(item[field]);
    //     const newVal = !currentVal;

    //     setOptimisticChecked((prev) => ({
    //         ...prev,
    //         [key]: newVal
    //     }));

    //     const targetId = itemId || (item.parentActionHighlightId as string | undefined) || activeActionHighlightId;
    //     if (!targetId) return;

    //     const payload = {
    //         name: (item.name || item.description) as string | undefined,
    //         ...currentActionObject,
    //         actionType: "Delivery Notes",
    //         data: {
    //             [field]: newVal,
    //             revisionId: getRefId(item.revisionId) || getRefId(item.revision),
    //             planCheckId: getRefId(item.planCheckId) || getRefId(item.planCheck),
    //             stageId: getRefId(item.stageId) || getRefId(item.stage),
    //         }
    //     };

    //     dispatch(editActionHighlight(payload, targetId, (res: ActionResponse) => {
    //         if (res?.data?.status || res?.status === 200) {
    //             refetchData();
    //         } else {
    //             setOptimisticChecked((prev) => ({
    //                 ...prev,
    //                 [key]: currentVal
    //             }));
    //         }
    //     }));
    // };

    const handleDeleteNote = (index: number) => {
        setDeleteIndex(index);
        setIsDeleteModelOpen(true);
    };

    const confirmDeleteNote = () => {
        if (deleteIndex === null) return;
        const item: Record<string, unknown> | undefined = descriptionList[deleteIndex] as Record<string, unknown> | undefined;
        if (!item) return;

        const noteItemId = (item._id || item.id) as string | undefined;
        const targetId = (item.parentActionHighlightId as string | undefined) || activeActionHighlightId || noteItemId;
        if (!noteItemId || !targetId) return;

        const payload = { id: noteItemId };
        dispatch(deleteActionHighlight(payload, targetId, (res: ActionResponse) => {
            if (res?.data?.status || res?.status === 200) {
                toast.success("Delivery note deleted");
                refetchData();
            } else {
                toast.error(res?.data?.message || "Failed to delete delivery note");
            }
        }));

        setDeleteIndex(null);
        setIsDeleteModelOpen(false);
    };

    const handleStartEdit = (index: number) => {
        editDraftRef.current = null;
        setEditingIndex(index);
    };

    const handleSaveEdit = (index: number, newText: string) => {
        if (!newText.trim()) return;
        const item: Record<string, unknown> | undefined = descriptionList[index] as Record<string, unknown> | undefined;
        if (!item) return;

        const noteItemId = (item._id || item.id) as string | undefined;
        const targetId = (item.parentActionHighlightId as string | undefined) || activeActionHighlightId || noteItemId;
        if (!targetId) {
            toast.error("Item ID not found");
            return;
        }

        const payload = {
            id: noteItemId,
            _id: noteItemId,
            name: item.name as string | undefined,
            actionType: selectedHighlight?.name || "Delivery Notes",
            projectId: currentActionObject?.projectId,
            stageId: currentActionObject?.stageId,
            taskId: currentActionObject?.taskId || null,
            data: {
                name: newText,
                revisionId: getRefId(item.revisionId) || getRefId(item.revision),
                planCheckId: getRefId(item.planCheckId) || getRefId(item.planCheck),
                stageId: getRefId(item.stageId) || getRefId(item.stage),
            }
        };

        dispatch(editActionHighlight(payload, targetId, (res: ActionResponse) => {
            if (res?.data?.status || res?.status === 200) {
                toast.success("Delivery note updated");
                refetchData();
            } else {
                toast.error(res?.data?.message || "Failed to update delivery note");
            }
        }));
        setEditingIndex(null);
    };

    const modalBg = colorMode === "light" ? "#ffffff" : "#1e293b";
    const textColor = colorMode === "light" ? "#0f172a" : "#f8fafc";
    const borderColor = colorMode === "light" ? "#e2e8f0" : "#334155";
    const subTextColor = colorMode === "light" ? "#64748b" : "#94a3b8";
    const thBackground = colorMode === "light" ? "#E2E6F1" : "#374358";
    const tdBackground = colorMode === "light" ? "#f7f9fe" : "transparent";
    const thColor = colorMode === "light" ? "black" : "#dadada";
    const tableBorderColor = colorMode === "light" ? "#d0d4db" : "#64748b";
    const completedGrey = colorMode === "light" ? "#6b7280" : "#f8fafc";
    const completedGreyBg = colorMode === "light" ? "rgba(107, 114, 128, 0.18)" : "rgba(255, 255, 255, 0.12)";
    const completedRowBg = "#283847";
    const columnWidths = activeTab > 0 ? ["10%", "14%", "11%", "52%", "13%"] : ["14%", "11%", "61%", "14%"];

    // same control look as the other detail-form dropdowns (e.g. Sales Representative in viewStages)
    const selectStyles = {
        control: (base: Record<string, unknown>) => ({
            ...base,
            backgroundColor: colorMode === "light" ? "#fff" : "#20283c",
            borderColor: colorMode === "light" ? "#f0ecec" : "#3d4a6b",
            boxShadow: "none",
            ":hover": { borderColor: colorMode === "light" ? "#999999" : "#52648e" },
            borderRadius: "10px",
            padding: "6px 8px",
            alignItems: "center",
            gap: "8px"
        })
    };

    const defaultStageName = useMemo(() => {
        const currentStgObj = stagesView as Record<string, unknown> | undefined;
        const stgDataObj = currentStgObj?.stageDataId as Record<string, unknown> | undefined;
        const tasksStgDataObj = (tasksView as Record<string, unknown>)?.stageDataId as Record<string, unknown> | undefined;
        const tasksStgObj = (tasksView as Record<string, unknown>)?.stageId as Record<string, unknown> | undefined;
        const actStgObj = actionObject?.stageId as Record<string, unknown> | undefined;

        return (
            (currentStgObj?.temporaryName || currentStgObj?.name ||
                stgDataObj?.temporaryName || stgDataObj?.name ||
                tasksStgDataObj?.temporaryName || tasksStgDataObj?.name ||
                tasksStgObj?.temporaryName || tasksStgObj?.name ||
                actStgObj?.temporaryName || actionObject?.temporaryName || actionObject?.stageName || actStgObj?.name) as string | undefined
        )?.trim() || "-";
    }, [stagesView, tasksView, actionObject]);

    const revPcOptions = useMemo(() => [
        { label: "Whole Stage", value: WHOLE_STAGE_OPTION },
        ...activeRevPcOptions
    ], [activeRevPcOptions]);

    const columns: ColumnDef<DescriptionItem>[] = useMemo(() => {
        const actionNameMap = new Map<string, string>();
        [...fetchedRevisions, ...fetchedPlanChecks].forEach((a) => {
            const name = a?.payload?.name || a?.name;
            if (name) {
                if (a._id) actionNameMap.set(a._id, name);
                if (a.id) actionNameMap.set(a.id, name);
            }
        });

        const resolveName = (val: unknown): string => {
            if (!val) return "";
            if (typeof val === "string") return actionNameMap.get(val) || (/^[0-9a-fA-F]{24}$/.test(val) ? "" : val);
            if (typeof val === "object") {
                const obj = val as { payload?: { temporaryName?: string; name?: string }; temporaryName?: string; name?: string };
                return obj.temporaryName || obj.payload?.temporaryName || obj.payload?.name || obj.name || "";
            }
            return "";
        };

        const isItemCompleted = (item: DescriptionItem) => activeTab > 0 && (item.isDone === true || [item.revisionId, item.planCheckId].some((ref) => {
            const payload = (ref as { payload?: { isCompleted?: boolean } } | undefined)?.payload;
            return typeof ref === "object" && payload?.isCompleted === true;
        }));

        const cols: ColumnDef<DescriptionItem>[] = [];

        if (activeTab > 0) {
            cols.push({
                id: "revision",
                header: "Stage/Rev/PC",
                cell: ({ row }) => {
                    const item = row.original;
                    const isRowCompleted = isItemCompleted(item);
                    const revName = resolveName(item.revisionId) || resolveName(item.planCheckId) || defaultStageName;
                    return (
                        <Flex sx={{ justifyContent: "center" }}>
                            <Box
                                className={isRowCompleted ? "dn-row-completed" : undefined}
                                title={revName}
                                sx={{
                                    display: "block",
                                    width: "100%",
                                    maxWidth: "110px",
                                    textAlign: "center",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    backgroundColor: "#7743DB",
                                    color: "#ffffff !important",
                                    fontWeight: 600,
                                    fontSize: "13px",
                                    px: "14px",
                                    py: "6px",
                                    borderRadius: "6px"
                                }}
                            >
                                {revName}
                            </Box>
                        </Flex>
                    );
                }
            });
        }

        cols.push(
            {
                id: "createdBy",
                header: "Added By",
                cell: ({ row }) => {
                    const item = row.original;
                    const raw = item.createdBy as string | undefined;
                    const isObjectId = raw ? /^[a-f\d]{24}$/i.test(raw) : false;

                    let displayName: string = '-';
                    if (raw && !isObjectId) {
                        displayName = raw;
                    } else if (raw && isObjectId) {
                        const users = Array.isArray(userData)
                            ? userData
                            : (userData as { data?: Array<{ _id: string; FirstName?: string; LastName?: string }> } | undefined)?.data || [];
                        const found = users.find(u => u._id === raw);
                        if (found) {
                            displayName = `${found.FirstName || ''} ${found.LastName || ''}`.trim() || '-';
                        }
                    }

                    const initials = displayName !== '-'
                        ? displayName.split(' ').filter(Boolean).map(p => p[0]).slice(0, 2).join('').toUpperCase()
                        : (item.userInitials || getUserInitials(raw));

                    return (
                        <Flex sx={{ alignItems: "center", gap: "10px" }}>
                            <Box
                                className="user-avatar-initials"
                                sx={{
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "50%",
                                    backgroundColor: "#7743DB",
                                    color: "#ffffff",
                                    fontWeight: 600,
                                    fontSize: "12px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0
                                }}
                            >
                                {initials}
                            </Box>
                            <Text sx={{ fontSize: "13px", fontWeight: 600, color: textColor }}>
                                {displayName}
                            </Text>
                        </Flex>
                    );
                }
            },
            {
                id: "createdAt",
                header: "Date",
                cell: ({ row }) => {
                    const item = row.original;
                    const createdAt = item.createdAt ? moment(item.createdAt) : moment();
                    return (
                        <Text sx={{ display: "block", textAlign: "center", fontSize: "13px", color: textColor }}>
                            {createdAt.format("MMM DD, YYYY")}
                            <br />
                            {createdAt.format("h:mm A")}
                        </Text>
                    );
                }
            },
            {
                id: "name",
                header: "Delivery Note",
                cell: ({ row }) => {
                    const index = row.index;
                    const item = row.original;
                    const isEditing = editingIndex === index;
                    const currentText = item.name || item.description || "";

                    if (isEditing) {
                        return (
                            <InlineEditCell
                                initialValue={editDraftRef.current ?? currentText}
                                originalValue={currentText}
                                onChange={(newText: string) => { editDraftRef.current = newText; }}
                                onSave={(newText: string) => handleSaveEdit(index, newText)}
                                onCancel={() => setEditingIndex(null)}
                                borderColor={borderColor}
                                colorMode={colorMode}
                                textColor={textColor}
                                isLoading={loader}
                            />
                        );
                    }

                    return (
                        <Text sx={{ fontSize: "13px", whiteSpace: "pre-line" }}>{linkifyText(currentText, "#6366f1")}</Text>
                    );
                }
            },
            {
                id: "actions",
                header: "Attachments / Actions",
                cell: ({ row }) => {
                    const index = row.index;
                    const item = row.original;
                    const isEditing = editingIndex === index;
                    const urls: string[] = Array.isArray(item.attachment) ? item.attachment : [];

                    return (
                        <Flex sx={{ alignItems: "center", justifyContent: "center", gap: "14px" }}>
                            <Box
                                onClick={() => {
                                    setRowAttachItem(item);
                                    setIsRowAttachLocked(isItemCompleted(item));
                                    setModalAttachments(urls);
                                    setModalUploadError("");
                                    setIsDraggingModal(false);
                                    setSelectedModalIndices([]);
                                    setIsAttachModalOpen(true);
                                }}
                                variant="cards.actionIcon"
                                // only re-enabled in completed rows when there is something to view
                                className={urls.length > 0 ? "dn-attach-btn" : undefined}
                                sx={{ width: "30px", justifyContent: "flex-start", position: "relative" }}
                                title={isItemCompleted(item) ? (urls.length > 0 ? "View Attachments" : "No Attachments") : urls.length > 0 ? "Manage Attachments" : "Add Attachments"}
                            >
                                <FaPaperclip size={16} style={{ color: "#6366f1", fill: "#6366f1", flexShrink: 0 }} />
                                {/* the count badge sits in the slot's top-right corner, clear of the icon; the slot width is fixed so the edit and delete icons stay in place */}
                                {urls.length > 0 && (
                                    <Badge variant="iconCount" className="dn-count-badge">
                                        {urls.length}
                                    </Badge>
                                )}
                            </Box>
                            <Box
                                onClick={() => (isEditing ? setEditingIndex(null) : handleStartEdit(index))}
                                variant="cards.actionIcon"
                                sx={{ color: isEditing ? "#7000FF" : subTextColor }}
                                title="Edit"
                            >
                                <FaPencilAlt size={16} />
                            </Box>
                            <Box
                                onClick={() => handleDeleteNote(index)}
                                variant="cards.actionIcon"
                                title="Delete"
                            >
                                <FaTrashAlt size={16} style={{ color: "#ef4444", fill: "#ef4444" }} />
                            </Box>
                        </Flex>
                    );
                }
            }
        );

        return cols;
    }, [activeTab, colorMode, subTextColor, textColor, borderColor, editingIndex, loader, fetchedRevisions, fetchedPlanChecks, defaultStageName]);

    const isTableLoading = isTabDataStale || (loader && !isAdding && !isModalSaving && !skipTableLoader);

    const currentSavedAttachments = rowAttachItem
        ? (Array.isArray((rowAttachItem as Record<string, unknown>).attachment)
            ? ((rowAttachItem as Record<string, unknown>).attachment as string[])
            : [])
        : attachments;
    const hasUnsavedModalChanges =
        modalAttachments.length !== currentSavedAttachments.length ||
        modalAttachments.some((url, i) => url !== currentSavedAttachments[i]);

    return (
        <>
            <Modal
                isOpen={showModal}
                toggle={toggleModal}
                centered
                scrollable
                size="xl"
                // narrower than the task view behind it (~93% of the screen) so it never sticks out past it
                style={{ maxWidth: "none", width: "93%", marginLeft: "auto", marginRight: "auto" }}
            >
                <ModalHeader
                    toggle={toggleModal}
                    close={
                        <Box
                            onClick={toggleModal}
                            sx={{ cursor: "pointer", color: textColor, fontSize: "18px", px: "8px", py: "4px" }}
                        >
                            &#x2715;
                        </Box>
                    }
                    style={{
                        backgroundColor: modalBg,
                        borderBottom: `1px solid ${borderColor}`,
                        color: textColor
                    }}
                >
                    <Flex sx={{ alignItems: "center", gap: "10px" }}>
                        <FaFileAlt style={{ color: "#eab308", fontSize: "20px" }} />
                        <Text sx={{ fontSize: "16px", fontWeight: 700, letterSpacing: "0.5px" }}>
                            DELIVERY NOTES
                        </Text>
                    </Flex>
                </ModalHeader>

                <ModalBody style={{ backgroundColor: modalBg, color: textColor, padding: "24px", position: "relative", overflow: "hidden" }}>
                    {isTableLoading && (
                        <Box
                            sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: colorMode === "light" ? "rgba(255, 255, 255, 0.6)" : "rgba(15, 23, 42, 0.6)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                zIndex: 1000,
                                borderRadius: "8px",
                                backdropFilter: "blur(1px)"
                            }}
                        >
                            <Spinner size={36} sx={{ color: "#7743DB" }} />
                        </Box>
                    )}
                    <Flex sx={{ borderBottom: `1px solid ${borderColor}`, mb: "24px", gap: "24px", overflowX: "auto" }}>
                        <Box
                            onClick={() => handleTabChange(0)}
                            sx={{
                                pb: "10px",
                                px: "4px",
                                cursor: "pointer",
                                borderBottom: activeTab === 0 ? "3px solid #7743DB" : "3px solid transparent",
                                color: activeTab === 0 ? "#7743DB" : subTextColor,
                                fontWeight: activeTab === 0 ? 600 : 400,
                                fontSize: "15px",
                                whiteSpace: "nowrap"
                            }}
                        >
                            PM
                        </Box>
                        {(taskTabs as TaskTabItem[])?.map((task: TaskTabItem, idx: number) => (
                            <Box
                                key={task.id || task._id || idx}
                                onClick={() => handleTabChange(idx + 1)}
                                sx={{
                                    pb: "10px",
                                    px: "4px",
                                    cursor: "pointer",
                                    borderBottom: activeTab === idx + 1 ? "3px solid #7743DB" : "3px solid transparent",
                                    color: activeTab === idx + 1 ? "#7743DB" : subTextColor,
                                    fontWeight: activeTab === idx + 1 ? 600 : 400,
                                    fontSize: "15px",
                                    whiteSpace: "nowrap"
                                }}
                            >
                                {task.name || task.label}
                            </Box>
                        ))}
                    </Flex>

                    <Flex sx={{ gap: "20px", alignItems: "stretch", mb: "20px", minHeight: "164px", flexWrap: "wrap" }}>
                        {activeTab > 0 && (
                            <Box sx={{ width: "240px", flexShrink: 0 }}>
                                <Label sx={{ fontSize: "14px", fontWeight: 600, mb: "6px", color: textColor }}>
                                    Stage
                                </Label>
                                <Input
                                    value={defaultStageName}
                                    disabled
                                    readOnly
                                    sx={{
                                        height: "50px",
                                        px: "16px",
                                        fontSize: "16px",
                                        borderRadius: "10px",
                                        // greyer / darker than the REV/PC dropdown so it reads as disabled; opacity 1 overrides the global input:disabled dimming
                                        color: colorMode === "light" ? "#6b7280" : "#9ca3af",
                                        backgroundColor: colorMode === "light" ? "#e2e5ea" : "#141a28",
                                        border: `1px solid ${colorMode === "light" ? "#c4c9d1" : "#3d4a6b"}`,
                                        opacity: "1 !important"
                                    }}
                                />
                                <Label sx={{ fontSize: "14px", fontWeight: 600, mt: "12px", mb: "6px", color: textColor }}>
                                    REV/PC <Text as="span" sx={{ color: "#ef4444" }}>*</Text>
                                </Label>
                                <FormSelect
                                    options={revPcOptions}
                                    value={effectiveRevision}
                                    styles={selectStyles}
                                    onChange={(selected: { value?: string } | null) => setSelectedRevision(selected?.value || "")}
                                    scrollable={true}
                                />
                            </Box>
                        )}

                        <Box sx={{ flex: "1 1 280px", minWidth: 0, display: "flex", flexDirection: "column" }}>
                            <Label sx={{ fontSize: "14px", fontWeight: 600, mb: "6px", color: textColor }}>
                                Delivery Note <Text as="span" sx={{ color: "#ef4444" }}>*</Text>
                            </Label>
                            <Textarea
                                placeholder="Write delivery note..."
                                value={noteText}
                                onChange={(e) => {
                                    setNoteText(e.target.value);
                                    if (e.target.value.trim()) setNoteError("");
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        if (!isAdding && !isAddingRef.current && !loader && !isModalUploading) {
                                            handleAddNote();
                                        }
                                    }
                                }}
                                rows={4}
                                sx={{
                                    width: "100%",
                                    padding: "10px 14px",
                                    borderRadius: "8px",
                                    border: `1px solid ${borderColor}`,
                                    backgroundColor: colorMode === "light" ? "#ffffff" : "#0f172a",
                                    color: textColor,
                                    fontSize: "14px",
                                    resize: "vertical",
                                    minHeight: "77px",
                                    flex: 1
                                }}
                            />
                            {noteError && (
                                <Text as="span" sx={{ fontSize: "12px", color: "#ef4444", mt: "4px", display: "block" }}>
                                    {noteError}
                                </Text>
                            )}
                        </Box>

                        {/* Add Button + Attach files stacked */}
                        <Flex sx={{ flexDirection: "column", alignItems: "stretch", width: "115px", gap: "12px", mt: "28px", flexShrink: 0, alignSelf: "flex-start" }}>
                            <Button
                                variant="SaveButton"
                                onClick={handleAddNote}
                                disabled={loader || isAdding || isModalUploading}
                                sx={{
                                    width: "100%",
                                    px: "16px",
                                    py: "8px",
                                    justifyContent: "center",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    cursor: (loader || isAdding || isModalUploading) ? "not-allowed" : "pointer",
                                    opacity: (loader || isAdding || isModalUploading) ? 0.7 : 1
                                }}
                            >
                                {isAdding ? <Spinner size={16} sx={{ color: "#ffffff" }} /> : "+ Add"}
                            </Button>
                            <Button
                                onClick={openAttachModal}
                                sx={{
                                    width: "100%",
                                    backgroundColor: colorMode === "light" ? "#F1F5F9" : "#334155",
                                    color: colorMode === "light" ? "#475569" : "#CBD5E1",
                                    border: `1px solid ${borderColor}`,
                                    borderRadius: "8px",
                                    px: "16px",
                                    py: "9px",
                                    fontSize: "12px",
                                    fontWeight: 500,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "6px",
                                    cursor: "pointer",
                                    whiteSpace: "nowrap",
                                    "&:hover": {
                                        backgroundColor: colorMode === "light" ? "#E2E8F0" : "#475569"
                                    }
                                }}
                            >
                                <FaPaperclip size={12} />
                                {attachments.length > 0 ? `${attachments.length}` : "Attach files"}
                            </Button>
                        </Flex>
                    </Flex>

                    {/* Info Note Banner */}
                    <Flex sx={{ alignItems: "center", gap: "8px", mb: "24px", color: subTextColor, fontSize: "13px" }}>
                        <FaInfoCircle style={{ color: "#64748b", flexShrink: 0 }} />
                        <Text sx={{ fontStyle: "normal" }}>
                            "Added By" and "Date" are generated automatically based on the user and the date/time when the item is added.
                        </Text>
                    </Flex>

                    {/* Table Container */}
                    <Box
                        sx={{
                            width: "100%",
                            // narrow screens: the table scrolls sideways instead of squeezing the columns or spilling out of the modal
                            "& .table-responsive": {
                                overflowX: "auto",
                                px: 0
                            },
                            "& .tableFixHead, & .tableNotFixHead": {
                                border: `1px solid ${tableBorderColor} !important`,
                                width: "100%",
                                display: "flex !important" as "flex",
                                flexDirection: "column !important" as "column",
                                // fit the viewport so the page never scrolls (global .tableFixHead has min-height: 50vh)
                                // the scrollable modal is as tall as the viewport (like the edit stage modal); the table takes the space left under the form
                                height: "max(160px, calc(100vh - 470px)) !important",
                                minHeight: "0 !important",
                                overflowY: "auto"
                            },
                            "& table": {
                                tableLayout: "fixed",
                                width: "100%",
                                minWidth: "860px",
                                alignSelf: (descriptionList.length === 0 ? "stretch !important" : "flex-start !important") as "stretch",
                                height: descriptionList.length === 0 ? "calc(100% - 40px) !important" : "auto !important"
                            },
                            // the store still holds the previous tab's notes until the new response arrives
                            "& tbody": {
                                visibility: isTableLoading ? "hidden" : "visible"
                            },
                            "& tbody, & tbody tr": {
                                height: descriptionList.length === 0 ? "100% !important" : "auto !important"
                            },
                            "& tbody td": {
                                height: descriptionList.length === 0 ? "100% !important" : "auto !important",
                                display: "table-cell !important",
                                verticalAlign: "middle !important",
                                padding: descriptionList.length === 0 ? "30px !important" : "16px !important",
                                // the empty "No Data Found" row fills the table, so a bottom line would leave a stray strip above the container border
                                borderBottom: descriptionList.length === 0 ? "none !important" : `1px solid ${tableBorderColor} !important`,
                                backgroundColor: `${tdBackground} !important`
                            },
                            "& tbody tr": {
                                borderBottom: "none !important"
                            },
                            // full grid: own borders between cells; hide the global column-edge pseudo lines
                            "& th, & td": {
                                wordBreak: "break-word",
                                textAlign: (descriptionList.length === 0 ? "center !important" : "left !important") as "left",
                                borderLeft: "none !important",
                                borderRight: `1px solid ${tableBorderColor} !important`
                            },
                            "& th:last-of-type, & td:last-of-type": {
                                borderRight: "none !important"
                            },
                            "& th::after, & td::after": {
                                display: "none !important"
                            },
                            ...Object.fromEntries(columnWidths.map((width, i) => [`& th:nth-of-type(${i + 1})`, { width }])),
                            // global table css pins the first two columns (sticky top/left), which detaches them from their row on vertical scroll
                            "& tbody td:nth-of-type(-n + 2)": {
                                position: "static !important" as "static"
                            },
                            // rows that are done or whose revision / plan check is completed: greyed out, all clicks disabled (attachments, edit, delete)
                            "& tr:has(.dn-row-completed) td, & tr:has(.dn-row-completed) td *:not(a)": {
                                color: `${completedGrey} !important`
                            },
                            "& tr:has(.dn-row-completed) td svg, & tr:has(.dn-row-completed) td svg *": {
                                fill: `${completedGrey} !important`
                            },
                            "& tr:has(.dn-row-completed) td .dn-row-completed, & tr:has(.dn-row-completed) td .user-avatar-initials": {
                                backgroundColor: `${completedGreyBg} !important`
                            },
                            ...(colorMode === "dark" && {
                                "& .table-hover > tbody > tr:has(.dn-row-completed) > td": {
                                    backgroundColor: `${completedRowBg} !important`
                                }
                            }),
                            // only the Stage/Rev/PC badge text, the rest of the greyed row keeps its colour
                            ...(colorMode === "light" && {
                                "& tr:has(.dn-row-completed) td .dn-row-completed": {
                                    color: "#ffffff !important",
                                    backgroundColor: `${completedGrey} !important`
                                }
                            }),
                            // the attachment count badge keeps white text on its own background in greyed rows
                            "& tr:has(.dn-row-completed) td .dn-count-badge": {
                                color: "#ffffff !important"
                            },
                            "& tr:has(.dn-row-completed)": {
                                pointerEvents: "none"
                            },
                            // links inside a greyed note keep their own colour and stay clickable
                            "& tr:has(.dn-row-completed) td a": {
                                pointerEvents: "auto"
                            },
                            // completed rows can still open their attachments (read-only in the attachments modal), only when they have any
                            "& tr:has(.dn-row-completed) td .dn-attach-btn": {
                                pointerEvents: "auto"
                            },
                            "& th": {
                                position: "sticky",
                                top: 0,
                                zIndex: 10,
                                backgroundColor: `${thBackground} !important`,
                                color: thColor,
                                padding: "8px 16px !important",
                                textAlign: "center !important" as "center",
                                fontWeight: 600,
                                borderBottom: `1px solid ${tableBorderColor} !important`
                            },
                            "& .table-hover > tbody > tr:hover > *": {
                                "--bs-table-bg-state": "transparent",
                                boxShadow: "none !important",
                                backgroundColor: `${tdBackground} !important`
                            },
                            // the shared table wraps header text in a span with its own text variant, so the weight is set on the span
                            "& th span": {
                                fontWeight: "600 !important"
                            }
                        }}
                    >
                        <DataTable
                            loader={false}
                            columns={columns}
                            data={descriptionList}
                            perPageCount={10}
                            currentPage={1}
                            showNavigation={false}
                            enableColumnFilters={false}
                            fixedColumn={false}
                            noDataCentered={true}
                            onPerPageChange={() => { }}
                            onPageChange={() => { }}
                        />
                    </Box>
                </ModalBody>
            </Modal>

            {/* Delete Confirmation Modal */}
            <DeletePopModule
                isDeleteModelOpen={isDeleteModelOpen}
                setIsDeleteModelOpen={setIsDeleteModelOpen}
                updatedDataFun={confirmDeleteNote}
                message="Are you sure you want to delete this delivery note?"
            />

            {/* Manage Attachments Modal */}
            <Modal
                isOpen={isAttachModalOpen}
                toggle={() => !isModalUploading && !isModalSaving && setIsAttachModalOpen(false)}
                centered
                size="md"
            >
                <ModalHeader
                    toggle={() => !isModalUploading && !isModalSaving && setIsAttachModalOpen(false)}
                    style={{ backgroundColor: modalBg, color: textColor }}
                >
                    {isRowAttachLocked ? "View Attachments" : "Manage Attachments"}
                </ModalHeader>
                <ModalBody style={{ backgroundColor: modalBg, color: textColor, padding: "24px" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                        {/* Current files list */}
                        {modalAttachments.length > 0 ? (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                <Flex sx={{ justifyContent: "space-between", alignItems: "center" }}>
                                    <Text sx={{ fontWeight: 600, fontSize: "14px", color: textColor }}>
                                        Current Attachments:
                                    </Text>
                                    {!isModalUploading && !isModalSaving && rowAttachItem && selectedModalIndices.length > 0 && (
                                        <Button
                                            type="button"
                                            disabled={isDownloadingFiles}
                                            onClick={async () => {
                                                const urls = selectedModalIndices.map((i) => modalAttachments[i]).filter(Boolean);
                                                if (!urls.length) return;
                                                setIsDownloadingFiles(true);
                                                try {
                                                    await downloadAttachments(urls);
                                                } catch {
                                                    /* ignore */
                                                } finally {
                                                    setIsDownloadingFiles(false);
                                                }
                                            }}
                                            sx={{
                                                fontSize: "12px",
                                                padding: "4px 10px",
                                                backgroundColor: "#7743DB",
                                                color: "#ffffff",
                                                borderRadius: "4px",
                                                cursor: isDownloadingFiles ? "not-allowed" : "pointer",
                                                border: "none",
                                                opacity: isDownloadingFiles ? 0.7 : 1
                                            }}
                                        >
                                            {isDownloadingFiles
                                                ? "Downloading..."
                                                : selectedModalIndices.length === 1
                                                    ? "Download"
                                                    : "Download All"}
                                        </Button>
                                    )}
                                </Flex>

                                {!isModalUploading && !isModalSaving && rowAttachItem && selectedModalIndices.length === 0 && (
                                    <Text sx={{ fontSize: "13px", color: "#ef4444", fontStyle: "italic", marginTop: "-4px" }}>
                                        * Please select checkbox to download attachments
                                    </Text>
                                )}

                                <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", maxHeight: "240px", overflowY: "auto", padding: "2px" }}>
                                    {modalAttachments.map((url, idx) => {
                                        const parts = url.split("/");
                                        const filename = parts[parts.length - 1];
                                        const ext = getFileExt(filename);
                                        const iconSrc = uploadIcon(ext);
                                        return (
                                            <Flex
                                                key={idx}
                                                sx={{
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    padding: "8px 12px",
                                                    borderRadius: "6px",
                                                    border: colorMode === "light" ? "1px solid #E2E8F0" : "1px solid #374151",
                                                    backgroundColor: colorMode === "light" ? "rgba(248,249,250,1)" : "rgba(13,202,240,0.12)",
                                                    gap: "12px"
                                                }}
                                            >
                                                <Flex sx={{ alignItems: "center", gap: "10px", minWidth: 0, flex: 1 }}>
                                                    {rowAttachItem && (
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedModalIndices.includes(idx)}
                                                            onChange={() =>
                                                                setSelectedModalIndices((prev) =>
                                                                    prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
                                                                )
                                                            }
                                                            disabled={isModalSaving || isModalUploading}
                                                            style={{
                                                                cursor: (isModalSaving || isModalUploading) ? "not-allowed" : "pointer",
                                                                flexShrink: 0,
                                                                width: "16px",
                                                                height: "16px",
                                                                accentColor: "#7743DB"
                                                            }}
                                                        />
                                                    )}
                                                    {iconSrc ? (
                                                        <img
                                                            src={iconSrc}
                                                            alt={ext}
                                                            style={{ width: "22px", height: "22px", objectFit: "contain", flexShrink: 0 }}
                                                        />
                                                    ) : (
                                                        <FaFileAlt size={20} color="#0dcaf0" style={{ flexShrink: 0 }} />
                                                    )}
                                                    <span
                                                        title={filename}
                                                        style={{
                                                            fontSize: "13px",
                                                            fontWeight: 500,
                                                            color: "#0dcaf0",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                            maxWidth: "250px",
                                                            display: "inline-block",
                                                            userSelect: "none"
                                                        }}
                                                    >
                                                        {`Link ${idx + 1}`}
                                                    </span>
                                                </Flex>
                                                {!isRowAttachLocked && (
                                                    <FaTimes
                                                        style={{
                                                            cursor: (isModalUploading || isModalSaving) ? "not-allowed" : "pointer",
                                                            color: "#ff4d4f",
                                                            opacity: (isModalUploading || isModalSaving) ? 0.5 : 0.85,
                                                            flexShrink: 0
                                                        }}
                                                        onClick={() => !isModalUploading && !isModalSaving && removeModalAttachment(idx)}
                                                        title="Remove file"
                                                    />
                                                )}
                                            </Flex>
                                        );
                                    })}
                                </Box>
                            </Box>
                        ) : (
                            <Text sx={{ fontSize: "14px", color: "#8F9195" }}>{isRowAttachLocked ? "No attachments." : "No attachments uploaded yet."}</Text>
                        )}

                        {/* Error Message */}
                        {modalUploadError && (
                            <Text sx={{ color: "#ef4444", fontSize: "13px" }}>{modalUploadError}</Text>
                        )}

                        {/* Upload Area with Drag & Drop (hidden for completed notes) */}
                        {!isRowAttachLocked && <Box
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => {
                                if (!isModalUploading && !isModalSaving) {
                                    document.getElementById("dn-modal-attachment-upload")?.click();
                                }
                            }}
                            variant={isDraggingModal ? "cards.uploadDropzoneActive" : "cards.uploadDropzone"}
                            sx={{
                                cursor: (isModalUploading || isModalSaving) ? "not-allowed" : "pointer",
                                border: isDraggingModal
                                    ? "2px dashed #0dcaf0"
                                    : (colorMode === "light" ? "2px dashed #cbd5e1" : "2px dashed rgba(255, 255, 255, 0.2)"),
                                backgroundColor: colorMode === "light" ? "#f8fafc" : "rgba(255, 255, 255, 0.02)"
                            }}
                        >
                            <input
                                type="file"
                                id="dn-modal-attachment-upload"
                                multiple
                                style={{ display: "none" }}
                                onChange={handleModalFileChange}
                                disabled={isModalUploading || isModalSaving}
                            />
                            <Label
                                htmlFor="dn-modal-attachment-upload"
                                variant="forms.UploadLabel"
                                sx={{ cursor: (isModalUploading || isModalSaving) ? "not-allowed" : "pointer", pointerEvents: "none" }}
                            >
                                {isModalUploading ? <Spinner size={16} /> : <FaPaperclip size={14} />}
                                {isModalUploading ? "Uploading..." : "Browse Files"}
                            </Label>
                            <Text variant="UploadHint">or drag and drop files here</Text>
                        </Box>}

                        {/* Action Buttons */}
                        <Flex sx={{ justifyContent: "flex-end", gap: "12px", mt: "8px", pt: "16px", borderTop: `1px solid ${borderColor}` }}>
                            <Button
                                type="button"
                                onClick={() => !isModalUploading && !isModalSaving && setIsAttachModalOpen(false)}
                                disabled={isModalUploading || isModalSaving}
                                sx={{
                                    background: "transparent",
                                    border: `1px solid ${borderColor}`,
                                    color: textColor,
                                    borderRadius: "8px",
                                    padding: "8px 16px",
                                    fontSize: "14px",
                                    cursor: (isModalUploading || isModalSaving) ? "not-allowed" : "pointer",
                                    opacity: (isModalUploading || isModalSaving) ? 0.6 : 1
                                }}
                            >
                                {isRowAttachLocked ? "Close" : "Cancel"}
                            </Button>
                            {!isRowAttachLocked && !isModalUploading && (hasUnsavedModalChanges || isModalSaving) && (
                                <Button
                                    type="button"
                                    onClick={saveModalAttachmentsLocal}
                                    disabled={isModalUploading || isModalSaving}
                                    sx={{
                                        background: "#7743DB",
                                        color: "#ffffff",
                                        borderRadius: "8px",
                                        padding: "8px 16px",
                                        fontSize: "14px",
                                        cursor: (isModalUploading || isModalSaving) ? "not-allowed" : "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        opacity: (isModalUploading || isModalSaving) ? 0.7 : 1
                                    }}
                                >
                                    {isModalSaving ? <Spinner size={16} sx={{ color: "#ffffff" }} /> : "Save"}
                                </Button>
                            )}
                        </Flex>
                    </Box>
                </ModalBody>
            </Modal>
        </>
    );
};

export default NewDeliveryNotesModal;
